class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.modal = this.closest(".search-modal");
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.suggestions = this.querySelector("[data-popular-search]");
    this.searchTerm = "";
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.querySelector("form.search").addEventListener(
      "submit",
      this.onFormSubmit.bind(this),
    );
    this.querySelector('button[type="button"]').addEventListener(
      "click",
      this.close.bind(this),
    );
    this.querySelector('button[type="reset"]').addEventListener(
      "click",
      this.clear.bind(this),
    );
    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    //this.addEventListener('keyup', this.onKeyup.bind(this));
    //  this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    // trim + on réduit les espaces multiples à un seul espace
    return this.input.value.trim().replace(/\s+/g, " ");
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-content")?.remove();
    }
    if (newSearchTerm != "") {
      this.querySelector('button[type="reset"]').classList.remove("hidden");
    } else {
      this.querySelector('button[type="reset"]').classList.add("hidden");
    }
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.clear();
      this.close(true);
      return;
    }
    this.getSearchResults(this.searchTerm);
    focusElement = this;
    trapFocusElements(this);
    setTimeout(() => {
      document
        .querySelector(this.querySelector("#Search-In-Template"))
        .setAttribute("tabindex", "0");
      document.querySelector(this.querySelector("#Search-In-Template")).focus();
    }, 100);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFocus() {
    document.body.classList.add("predictive-search--focus");
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
      this.close(true);
    }
    event.preventDefault();
    switch (event.code) {
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );
    if (selectedProduct) selectedProduct.click();
  }

  // ---- Recherche locale par sous-chaîne dans tout le catalogue (SKU + titre) ----
  // Utilisée en dernier recours quand aucune variante de casse/espace ne trouve rien
  static normalize(str) {
    return (str || "").toUpperCase().replace(/\s+/g, "");
  }

  static fetchProductIndex() {
    if (PredictiveSearch.productIndexPromise) {
      return PredictiveSearch.productIndexPromise;
    }

    const cacheKey = "skuSearchIndex";
    const cacheTTL = 1000 * 60 * 60 * 6; // 6 heures
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || "null");
      if (cached && Date.now() - cached.timestamp < cacheTTL) {
        PredictiveSearch.productIndexPromise = Promise.resolve(cached.data);
        return PredictiveSearch.productIndexPromise;
      }
    } catch (e) {
      /* sessionStorage indisponible, on continue sans cache */
    }

    PredictiveSearch.productIndexPromise = (async () => {
      const index = [];
      let page = 1;
      const maxPages = 20; // jusqu'à 5000 produits (250 x 20)
      while (page <= maxPages) {
        const res = await fetch(`/products.json?limit=250&page=${page}`);
        if (!res.ok) break;
        const data = await res.json();
        const products = data.products || [];
        if (!products.length) break;
        products.forEach((p) => {
          (p.variants || []).forEach((v) => {
            index.push({ sku: v.sku || "", title: p.title, handle: p.handle });
          });
        });
        if (products.length < 250) break;
        page++;
      }
      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ timestamp: Date.now(), data: index }),
        );
      } catch (e) {
        /* ignore */
      }
      return index;
    })();

    return PredictiveSearch.productIndexPromise;
  }

  async localFallbackSearch(term) {
    const target = PredictiveSearch.normalize(term);
    if (!target) return [];
    const index = await PredictiveSearch.fetchProductIndex();
    const matches = index.filter((item) => {
      const skuNorm = PredictiveSearch.normalize(item.sku);
      const titleNorm = PredictiveSearch.normalize(item.title);
      return (skuNorm && skuNorm.includes(target)) || titleNorm.includes(target);
    });
    const seen = new Set();
    return matches
      .filter((m) => {
        if (seen.has(m.handle)) return false;
        seen.add(m.handle);
        return true;
      })
      .slice(0, 10);
  }

  renderFallbackResults(matches) {
    if (this.suggestions) this.suggestions.classList.add("hidden");
    if (!matches.length) {
      this.removeAttribute("results");
      this.removeAttribute("loading");
      return;
    }
    const items = matches
      .map(
        (m) => `
      <li role="option" style="padding:10px 16px;border-bottom:1px solid #eee;">
        <a href="/products/${m.handle}" style="display:block;color:inherit;text-decoration:none;">
          <span style="font-weight:600;">${m.title}</span>
          ${m.sku ? `<span style="display:block;font-size:12px;opacity:.6;">SKU: ${m.sku}</span>` : ""}
        </a>
      </li>`,
      )
      .join("");
    this.predictiveSearchResults.innerHTML = `<ul role="listbox" style="list-style:none;margin:0;padding:0;">${items}</ul>`;
    this.setAttribute("results", true);
    this.removeAttribute("loading");
    if (this.modal) this.modal.classList.add("searching");
  }
  // ---- Fin recherche locale ----

  // ---- Nouvelle logique : génère plusieurs variantes (casse / espaces) ----
  buildSearchVariants(term) {
    const variants = new Set();
    const clean = term.trim().replace(/\s+/g, " "); // espaces multiples -> un seul

    // Variantes de base (avec espace normal)
    variants.add(clean);
    variants.add(clean.toUpperCase());
    variants.add(clean.toLowerCase());

    // Variantes sans aucun espace (ex: SKU tapé "AB 123" mais stocké "AB123")
    const noSpace = clean.replace(/\s+/g, "");
    variants.add(noSpace);
    variants.add(noSpace.toUpperCase());
    variants.add(noSpace.toLowerCase());

    // Si plusieurs mots, on essaie aussi chaque mot individuellement
    // (utile si l'utilisateur tape juste "handles" pour "SHAPE HANDLES")
    const words = clean.split(" ").filter((w) => w.length > 1);
    if (words.length > 1) {
      words.forEach((w) => {
        variants.add(w);
        variants.add(w.toUpperCase());
        variants.add(w.toLowerCase());
      });
    }

    return Array.from(variants);
  }

  hasProducts(markup) {
    // heuristique simple : on vérifie s'il y a un lien vers une fiche produit
    return markup.includes('href="/products/') || markup.includes("href='/products/");
  }

  getSearchResults(searchTerm) {
    const variants = this.buildSearchVariants(searchTerm);
    this.setLiveRegionLoadingState();
    this.tryVariants(variants, 0, searchTerm);
  }

  tryVariants(variants, index, originalTerm) {
    if (index >= variants.length) return;

    const term = variants[index];
    const isLast = index === variants.length - 1;
    const queryKey = term.replace(" ", "-").toLowerCase();

    const handleNoMatch = (resultsMarkup) => {
      if (!isLast) {
        this.tryVariants(variants, index + 1, originalTerm);
        return;
      }
      // Toutes les variantes ont échoué : on tente la recherche locale par sous-chaîne
      this.localFallbackSearch(originalTerm).then((matches) => {
        if (matches.length) {
          this.renderFallbackResults(matches);
        } else {
          this.finishRender(resultsMarkup);
        }
      });
    };

    if (this.cachedResults[queryKey]) {
      if (this.hasProducts(this.cachedResults[queryKey])) {
        this.finishRender(this.cachedResults[queryKey]);
      } else {
        handleNoMatch(this.cachedResults[queryKey]);
      }
      return;
    }

    fetch(
      `${window.routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=predictive-search`,
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;

        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );

        if (this.hasProducts(resultsMarkup)) {
          this.finishRender(resultsMarkup);
        } else {
          handleNoMatch(resultsMarkup);
        }
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  finishRender(resultsMarkup) {
    if (this.suggestions) {
      this.suggestions.classList.add("hidden");
    }
    this.renderSearchResults(resultsMarkup);
    if (this.modal) this.modal.classList.add("searching");
    if (typeof animationCheck !== "undefined" && animationCheck && typeof AOS !== "undefined") {
      AOS.refreshHard();
    }
  }
  // ---- Fin nouvelle logique ----

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector("[data-predictive-search]");
    this.setLiveRegionText(storeLoader);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statuscontent) {
    this.statusElement.setAttribute("aria-hidden", "false");
    if (this.suggestions) {
      this.suggestions.classList.add("populat-content-blur");
    }
    this.statusElement.innerHTML = statuscontent;
    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    this.setLiveRegionResults();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);
    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.input.setAttribute("aria-expanded", false);
    document.body.classList.remove("predictive-search--focus");
  }

  clear(event) {
    this.input.value = "";
    this.querySelector("[data-predictive-search]").innerHTML = "";
    this.input.focus();
    if (this.suggestions) {
      this.suggestions.classList.remove("hidden", "populat-content-blur");
    }
    if (this.modal) this.modal.classList.remove("searching");
  }
}

customElements.define("predictive-search", PredictiveSearch);
